<template>
  <div class="committer-summary-table">
    <a-table :columns="columns" :data-source="tableData" :loading="props.loading" :pagination="false" :row-key="rowKey"
      size="small" :scroll="{ x: 2200, y: 560 }">
      <template #bodyCell="{ column, record }">
        <span v-if="column.dataIndex === 'committer'">
          <span class="committer-id">{{ record.committer }}</span>
          <span v-if="record.email" class="committer-email">&lt;{{ record.email }}&gt;</span>
        </span>
        <span v-else-if="column.dataIndex === 'repoPath'" class="repo-path-cell">
          <template v-for="(line, idx) in repoLines(record.repoPath)" :key="idx">
            <span>{{ line }}</span><br />
          </template>
        </span>
        <span v-else-if="column.dataIndex === 'employerType'">
          <a-tag :color="employerTypeMeta(record.employerType).color">
            {{ employerTypeMeta(record.employerType).text }}
          </a-tag>
        </span>
        <span v-else-if="['changedAdditions', 'changedDeletions', 'prReviewCount', 'commentReviewCount'].includes(column.dataIndex)">
          {{ formatInteger(record[column.dataIndex]) }}
        </span>
        <span v-else-if="column.dataIndex === 'prReviewTime'">
          {{ formatDecimal(record.prReviewTime) }}
        </span>
      </template>
    </a-table>

    <div class="pagination-footer">
      <div class="pagination-summary">
        第 {{ props.pageVo.startIndex + 1 }}—{{ props.pageVo.endIndex }} 个 committers，共
        {{ props.pageVo.totalRows }} 个 committers
      </div>
      <a-pagination :current="props.pageVo.curPage" :page-size="props.pageVo.pageSize"
        :page-size-options="['10', '20', '50', '100']" :total="props.pageVo.totalRows" show-size-changer show-quick-jumper
        @change="handlePageChange" @showSizeChange="handlePageSizeChange" />
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed } from 'vue';
import dayjs from 'dayjs';

const props = defineProps({
  records: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  pageVo: {
    type: Object,
    default: () => ({
      totalRows: 0,
      curPage: 1,
      pageSize: 10,
      startIndex: 0,
      endIndex: 0,
    }),
  },
});

const emit = defineEmits(['page-change', 'page-size-change']);

const employerTypeMeta = (employerType) => {
  if (employerType === 'HUAWEI') {
    return { text: '华为', color: 'red' };
  }
  if (employerType === 'NON_HUAWEI') {
    return { text: '非华为', color: 'blue' };
  }
  return { text: employerType || '-', color: 'default' };
};

const getEarliestTime = (details) => {
  const validTimes = details
    .map((detail) => detail?.startTime)
    .filter(Boolean)
    .sort((left, right) => dayjs(left).valueOf() - dayjs(right).valueOf());

  return validTimes[0] || null;
};

const getLatestTime = (details) => {
  const validTimes = details
    .map((detail) => detail?.endTime)
    .filter(Boolean)
    .sort((left, right) => dayjs(right).valueOf() - dayjs(left).valueOf());

  return validTimes[0] || null;
};

const sumNumberField = (details, fieldName) => {
  return details.reduce((sum, detail) => sum + Number(detail?.[fieldName] || 0), 0);
};

// 汇总页优先吃后端“总计”字段（total/summary），
// 仅在后端未返回时，回退到前端基于 details 的兼容计算。
const getTotalField = (item, fieldName) => {
  const total = item?.total || item?.summary || {};
  if (total[fieldName] !== undefined && total[fieldName] !== null) {
    return total[fieldName];
  }
  if (item?.[fieldName] !== undefined && item?.[fieldName] !== null) {
    return item[fieldName];
  }
  return null;
};

const formatInteger = (value) => {
  return Number(value || 0).toLocaleString('en-US');
};

const formatDecimal = (value) => {
  return Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const tableData = computed(() => {
  return props.records.map((item) => {
    const details = Array.isArray(item?.details) ? item.details : [];
    // 统一先拿总计字段，减少重复判空和重复聚合。
    const totalRepoPath = getTotalField(item, 'repoPath');
    const totalStartTime = getTotalField(item, 'startTime');
    const totalEndTime = getTotalField(item, 'endTime');
    const totalChangedAdditions = getTotalField(item, 'changedAdditions');
    const totalChangedDeletions = getTotalField(item, 'changedDeletions');
    const totalPrReviewTime = getTotalField(item, 'prReviewTime');
    const totalPrReviewCount = getTotalField(item, 'prReviewCount');
    const totalCommentReviewCount = getTotalField(item, 'commentReviewCount');

    return {
      committer: item?.committer || '',
      email: item?.email || '',
      employerType: item?.employerType || '',
      // 规则：总计优先，details 兜底。
      repoPath: totalRepoPath ?? details.map((detail) => detail?.repoPath).filter(Boolean).join('\n'),
      startTime: totalStartTime ?? getEarliestTime(details),
      endTime: totalEndTime ?? getLatestTime(details),
      changedAdditions: Number(totalChangedAdditions ?? sumNumberField(details, 'changedAdditions')),
      changedDeletions: Number(totalChangedDeletions ?? sumNumberField(details, 'changedDeletions')),
      prReviewTime: Number(totalPrReviewTime ?? sumNumberField(details, 'prReviewTime')),
      prReviewCount: Number(totalPrReviewCount ?? sumNumberField(details, 'prReviewCount')),
      commentReviewCount: Number(totalCommentReviewCount ?? sumNumberField(details, 'commentReviewCount')),
    };
  });
});

// 计算 repoPath 换行用
const repoLines = (repoPath) => {
  if (!repoPath) return [];
  return repoPath.split('\n');
};

const columns = [
  {
    title: 'Committer',
    dataIndex: 'committer',
    key: 'committer',
    width: 320,
    customRender: ({ record }) => {
      return record.email ? `${record.committer} <${record.email}>` : record.committer;
    },
  },
  {
    title: '雇主类型',
    dataIndex: 'employerType',
    key: 'employerType',
    width: 130,
  },
  {
    title: '代码仓',
    dataIndex: 'repoPath',
    key: 'repoPath',
    className: 'repo-col',
    width: 520,
  },
  {
    title: '任职开始时间',
    dataIndex: 'startTime',
    key: 'startTime',
    width: 130,
    customRender: ({ text }) => (text ? dayjs(text).format('YYYY-MM-DD') : '至今'),
  },
  {
    title: '任职结束时间',
    dataIndex: 'endTime',
    key: 'endTime',
    width: 130,
    customRender: ({ text }) => (text ? dayjs(text).format('YYYY-MM-DD') : '至今'),
  },
  {
    title: '检视 PR 新增代码量（行）',
    dataIndex: 'changedAdditions',
    key: 'changedAdditions',
    width: 190,
  },
  {
    title: '检视 PR 删除代码量（行）',
    dataIndex: 'changedDeletions',
    key: 'changedDeletions',
    width: 190,
  },
  {
    title: '检视 PR 个数',
    dataIndex: 'prReviewCount',
    key: 'prReviewCount',
    width: 140,
  },
  {
    title: '检视时长（小时）',
    dataIndex: 'prReviewTime',
    key: 'prReviewTime',
    width: 150,
  },
  {
    title: '检视评论个数',
    dataIndex: 'commentReviewCount',
    key: 'commentReviewCount',
    width: 150,
  },
];

const handlePageChange = (page) => {
  emit('page-change', page);
};

const handlePageSizeChange = (current, size) => {
  emit('page-size-change', current, size);
};

const rowKey = (record) => `${record.committer || ''}_${record.email || ''}`;
</script>

<style scoped>
.committer-summary-table {
  width: 100%;
}

.pagination-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 16px;
}

.pagination-summary {
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
}

.repo-path-cell {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  white-space: nowrap;
}

.committer-summary-table :deep(.ant-table-cell) {
  white-space: nowrap;
}

.committer-id {
  font-weight: 600;
  color: #1f2937;
}

.committer-email {
  margin-left: 6px;
  color: #6b7280;
  font-size: 12px;
}
</style>